import React, { useState } from 'react';
import { useBrand } from '../../../app/config/brand';
import { Button } from '../../../shared/components/ui/Button';
import { Input } from '../../../shared/components/ui/Input';
import type { AccountAttorney } from '../../../api/useAttorneyApi';
import { useAttorneyApi } from '../../../api/useAttorneyApi';

interface AttorneyFormModalProps {
  attorney?: AccountAttorney;
  onSave: () => void;
  onClose: () => void;
  onDelete?: (id: string) => void;
}

const AttorneyFormModal: React.FC<AttorneyFormModalProps> = ({ attorney, onSave, onClose, onDelete }) => {
  const brand = useBrand();
  const { createAttorney, updateAttorney, lookupAttorney, loading } = useAttorneyApi();
  const isEditing = !!attorney;

  const [barNumber, setBarNumber] = useState(attorney?.barNumber || '');
  const [barState] = useState(attorney?.barState || 'CA');
  const [firstName, setFirstName] = useState(attorney?.firstName || '');
  const [middleName, setMiddleName] = useState(attorney?.middleName || '');
  const [lastName, setLastName] = useState(attorney?.lastName || '');
  const [suffix, setSuffix] = useState(attorney?.suffix || '');
  const [firmName, setFirmName] = useState(attorney?.firmName || '');
  const [email, setEmail] = useState(attorney?.email || '');
  const [phone, setPhone] = useState(attorney?.phone || '');
  const [lookupStatus, setLookupStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleLookup = async () => {
    if (!barNumber.trim()) return;
    setLookupStatus(null);
    try {
      const result = await lookupAttorney(barNumber);
      if (result.data && result.data.length > 0) {
        const att = result.data[0];
        if (att.firstName) setFirstName(att.firstName);
        if (att.lastName) setLastName(att.lastName);
        if (att.middleName) setMiddleName(att.middleName);
        if (att.firmName) setFirmName(att.firmName);
        setLookupStatus('found');
      } else {
        setLookupStatus('not-found');
      }
    } catch {
      setLookupStatus('error');
    }
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim() || !barNumber.trim()) return;
    setSaving(true);
    try {
      const data = { barNumber, barState, firstName, middleName, lastName, suffix, firmName, email, phone };
      if (isEditing && attorney) {
        await updateAttorney(attorney.id, data);
      } else {
        await createAttorney(data);
      }
      onSave();
    } catch {
      // error state handled by hook
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = () => {
    if (attorney && onDelete) {
      onDelete(attorney.id);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" role="button" tabIndex={0} aria-label="Close modal" onClick={onClose} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }} />
      <div
        className="relative w-full max-w-lg rounded-xl shadow-lg p-6 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: brand.colors.bgCard }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold" style={{ color: brand.colors.secondary }}>
            {isEditing ? 'Edit Attorney' : 'Add Attorney'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        {showDeleteConfirm ? (
          <div className="text-center py-6">
            <p className="text-lg mb-4" style={{ color: brand.colors.text }}>
              Are you sure you want to delete this attorney?
            </p>
            <p className="text-sm mb-6" style={{ color: brand.colors.textLight }}>
              {attorney?.firstName} {attorney?.lastName} ({attorney?.barNumber})
            </p>
            <div className="flex justify-center gap-3">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button onClick={handleDeleteConfirm} style={{ backgroundColor: brand.colors.error }}>Delete</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label htmlFor="attorney-barNumber" className="block text-sm font-medium mb-1.5" style={{ color: brand.colors.text }}>Bar Number</label>
              <div className="flex gap-2">
                <input
                  id="attorney-barNumber"
                  data-testid="attorney-bar-number"
                  className="flex-1 px-4 py-2.5 rounded-lg border text-sm focus:outline-hidden focus:ring-2 transition-all"
                  style={{ borderColor: brand.colors.border }}
                  value={barNumber}
                  onChange={(e) => setBarNumber(e.target.value)}
                  placeholder="Enter bar number"
                />
                <Button
                  data-testid="attorney-lookup-button"
                  variant="secondary"
                  size="sm"
                  onClick={handleLookup}
                  disabled={loading || !barNumber.trim()}
                >
                  {loading ? 'Looking up...' : 'Look Up'}
                </Button>
              </div>
              {lookupStatus === 'found' && (
                <p className="text-sm mt-1" style={{ color: brand.colors.success }}>Attorney found - fields populated</p>
              )}
              {lookupStatus === 'not-found' && (
                <p className="text-sm mt-1" style={{ color: brand.colors.warning }}>No attorney found with that bar number</p>
              )}
              {lookupStatus === 'error' && (
                <p className="text-sm mt-1" style={{ color: brand.colors.error }}>Lookup failed - please try again</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <Input label="First Name" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              <Input label="Last Name" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Input label="Middle Name" value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
              <Input label="Suffix" value={suffix} onChange={(e) => setSuffix(e.target.value)} />
            </div>
            <Input label="Firm Name" className="mb-4" value={firmName} onChange={(e) => setFirmName(e.target.value)} />
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <Input label="Phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>

            <div className="flex justify-between">
              <div>
                {isEditing && onDelete && (
                  <Button
                    variant="text"
                    onClick={() => setShowDeleteConfirm(true)}
                    style={{ color: brand.colors.error }}
                  >
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                  data-testid="save-attorney-button"
                  onClick={handleSave}
                  disabled={saving || !firstName.trim() || !lastName.trim() || !barNumber.trim()}
                >
                  {saving ? 'Saving...' : (isEditing ? 'Update' : 'Add Attorney')}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AttorneyFormModal;
